import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ScrollText } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import api from '@/lib/api';
import { ContractStatus } from '@hirenfire/shared';
import type { ApiResponse, ContractWithParties } from '@hirenfire/shared';

type BadgeVariant = 'warning' | 'success' | 'error' | 'default' | 'primary' | 'purple';

const statusVariant: Record<ContractStatus, BadgeVariant> = {
  [ContractStatus.PENDING_ESCROW]: 'warning',
  [ContractStatus.ACTIVE]: 'success',
  [ContractStatus.PAUSED]: 'default',
  [ContractStatus.COMPLETED]: 'primary',
  [ContractStatus.CANCELLED]: 'error',
  [ContractStatus.DISPUTED]: 'error',
};

export function ContractsPage() {
  const [contracts, setContracts] = useState<ContractWithParties[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<ContractStatus | 'ALL'>('ALL');

  useEffect(() => {
    async function fetchContracts() {
      try {
        const params = activeFilter !== 'ALL' ? `?status=${activeFilter}` : '';
        const res = await api.get<ApiResponse<ContractWithParties[]>>(`/contracts${params}`);
        if (res.data.data) setContracts(res.data.data);
      } catch {
        // fallback
      } finally {
        setIsLoading(false);
      }
    }
    fetchContracts();
  }, [activeFilter]);

  const filters: Array<{ label: string; value: ContractStatus | 'ALL' }> = [
    { label: 'All', value: 'ALL' },
    { label: 'Active', value: ContractStatus.ACTIVE },
    { label: 'Pending Escrow', value: ContractStatus.PENDING_ESCROW },
    { label: 'Completed', value: ContractStatus.COMPLETED },
    { label: 'Disputed', value: ContractStatus.DISPUTED },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-white">Contracts</h1>
        <p className="text-gray-400 mt-1">Manage your active and past contracts</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {filters.map((filter) => (
          <Button
            key={filter.value}
            variant={activeFilter === filter.value ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => {
              setActiveFilter(filter.value);
              setIsLoading(true);
            }}
          >
            {filter.label}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Spinner size="lg" />
        </div>
      ) : contracts.length === 0 ? (
        <Card>
          <CardBody className="py-16 text-center">
            <ScrollText className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No contracts found</p>
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-3">
          {contracts.map((contract) => (
            <Link key={contract.id} to={`/contracts/${contract.id}`}>
              <Card className="hover:border-dark-500 transition-all mb-3">
                <CardBody>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-white">{contract.job.title}</p>
                      <p className="text-sm text-gray-400 mt-0.5">
                        {contract.agency.displayName} &rarr; {contract.worker.displayName}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Started {new Date(contract.startDate).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <Badge variant={statusVariant[contract.status]}>{contract.status}</Badge>
                      <span className="text-sm font-semibold text-white">
                        ${contract.totalAmount} {contract.currency}
                      </span>
                      {contract.escrowWallet && (
                        <span className="text-xs text-gray-500">
                          Escrow: ${contract.escrowWallet.balance}
                        </span>
                      )}
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
